import { useEffect } from 'react';
import { useStableCallback } from './useStableCallback';

/**
 * Run `dismiss` when the owning view stops being the selected one.
 *
 * Views such as Assistant stay mounted behind whatever the user navigates to and
 * are hidden with CSS, so their history, scroll position and open source card
 * survive the trip. Overlays portaled to `document.body` escape that hiding: they
 * keep covering the new page, with their own dismiss controls belonging to a view
 * the user has already left (TR-3219). Their owner has to close them explicitly.
 *
 * Gate on the selected *nav view*, not on a general "is visible" flag — a
 * fullscreen overlay opened from within the view also hides it, and must not tear
 * down the overlay stack underneath itself.
 *
 * `dismiss` may be a fresh closure each render; only `viewSelected` gates the
 * effect, so re-rendering while the view is already gone will not re-run it.
 */
export function useDismissOnViewExit(viewSelected: boolean, dismiss: () => void): void {
  const stableDismiss = useStableCallback(dismiss);

  useEffect(() => {
    if (viewSelected) return;
    stableDismiss();
  }, [viewSelected, stableDismiss]);
}
